import classNames from "classnames";
import React, { FC, useState } from "react";
import { LuX } from "react-icons/lu";

import {
  bannerFor,
  DISMISS_STORAGE_KEY,
  localDayKey,
  SubscriptionStatus,
} from "./subscription.utils";
import { useBillingRedirects } from "./useSubscription";

const readDismissed = (): string | null => {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY);
  } catch {
    return null;
  }
};

const TONES = {
  purple: "border-[#DDD5FF] bg-[#F3F0FF] text-[#3A2BA0]",
  orange: "border-[#FBD8B4] bg-[#FFF4E8] text-[#8A4A0B]",
  red: "border-[#F5C2C2] bg-[#FDF0F0] text-[#9B1C1C]",
};
const BUTTONS = {
  purple: "bg-[#6B4EFF] hover:bg-[#5A3EEB]",
  orange: "bg-[#E8780C] hover:bg-[#CF6A08]",
  red: "bg-[#C62828] hover:bg-[#A92222]",
};

/**
 * Trial / payment banner shown at the top of every page (seat billing only):
 * purple in the last 3 days of the trial (dismissible for the day), orange
 * during the 4 grace days (not dismissible), red when a payment failed.
 */
export const SubscriptionBanner: FC<{
  status?: SubscriptionStatus;
  orgId: string;
}> = ({ status, orgId }) => {
  const [dismissed, setDismissed] = useState(readDismissed);
  const today = localDayKey(new Date());
  const banner = bannerFor(status, dismissed, today);
  const { addPaymentMethod, openPortal, busy, error } =
    useBillingRedirects(orgId);
  if (!banner) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, today);
    } catch {
      // private window: dismissed until the page reloads
    }
    setDismissed(today);
  };

  return (
    <div
      role={"status"}
      data-testid={`subscription-banner-${banner.kind}`}
      className={classNames(
        "mb-4 flex flex-wrap items-center gap-3 rounded-[14px] border px-4 py-3 font-Manrope text-[13.5px] font-semibold",
        TONES[banner.tone],
      )}>
      <span className={"min-w-0 flex-1"}>{banner.text}</span>
      {error && (
        <span className={"text-[12.5px] font-bold text-[#B42318]"}>
          {error}
        </span>
      )}
      {banner.action && (
        <button
          type={"button"}
          disabled={busy}
          onClick={() =>
            banner.action === "portal"
              ? void openPortal()
              : void addPaymentMethod()
          }
          className={classNames(
            "h-[34px] shrink-0 rounded-lg px-4 text-[13px] font-bold text-white disabled:opacity-60",
            BUTTONS[banner.tone],
          )}>
          {busy ? "Opening…" : banner.actionLabel}
        </button>
      )}
      {banner.dismissible && (
        <button
          type={"button"}
          aria-label={"Hide for today"}
          title={"Hide for today"}
          onClick={dismiss}
          className={"shrink-0 opacity-60 hover:opacity-100"}>
          <LuX size={16} />
        </button>
      )}
    </div>
  );
};
